import { BaseTag, RiskTag, TagSeverity } from '@praetorian-chariot/ui';
import { ReactNode } from 'react';

import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import {
  Access,
  EntityHistory,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
  RiskStatusWithoutSeverity,
} from '@/types';
import { formatDate } from '@/utils/date.util';
import { getAccessEmail } from '@/utils/getAccessEmail';
import { getMachineRiskComment } from '@/utils/risk.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';

const EmptySpace = ' ';

function isLikelyMarkdown(text: string | null | undefined): boolean {
  if (!text) return false;
  // This regex looks for common markdown patterns.
  // It's a heuristic and might not be perfect.
  const markdownRegex = new RegExp(
    [
      '(^|\\n)#{1,6}\\s', // Headers
      '(^|\\n)[\\*\\-\\+]\\s', // Unordered list items
      '(^|\\n)\\d+\\.\\s', // Ordered list items
      '(^|\\n)>\\s', // Blockquotes
      '```', // Code blocks (start or end)
      '!\\[[^\\]]*\\]\\([^\\)]*\\)', // Images ![alt](url)
      '\\[[^\\]]+\\]\\([^\\)]+\\)', // Links [text](url)
      '\\*\\*([^\\*\\*]+)\\*\\*', // Bold **text** (non-empty)
      '__([^__]+)__', // Bold __text__ (non-empty)
      '`([^`]+)`', // Inline code `text` (non-empty)
    ].join('|'),
    'gm' // global for line starts (^), multiline for \n
  );
  return markdownRegex.test(text);
}

interface HistoryDiffResult {
  title: ReactNode;
  updated: string;
}

const severityMap: Record<RiskSeverity, TagSeverity> = {
  [RiskSeverity.Critical]: 'critical',
  [RiskSeverity.High]: 'high',
  [RiskSeverity.Medium]: 'medium',
  [RiskSeverity.Low]: 'low',
  [RiskSeverity.Exposure]: 'exposure',
  [RiskSeverity.Info]: 'info',
};

export function getHistoryDiff(
  historyItem: EntityHistory,
  isFirst: boolean,
  accesss: Access[] = []
): HistoryDiffResult {
  if (isFirst) {
    const toStatus = historyItem.to as string;
    if (!toStatus) {
      return { title: '', updated: '' };
    }

    const { severity: toSeverity } = getRiskStatusLabel(toStatus);
    if (!toSeverity) {
      return { title: '', updated: '' };
    }

    const result = {
      title: (
        <div className="whitespace-break-spaces">
          <p className="inline">
            <strong>First Tracked</strong>
            {EmptySpace}as{EmptySpace}
          </p>
          <RiskTag severity={severityMap[toSeverity]} size="sm" />
        </div>
      ),
      updated: formatDate(historyItem.updated),
    };
    return result;
  }

  const { status: statusFrom, severity: severityFrom } = getRiskStatusLabel(
    historyItem.from
  );
  const { status: statusTo, severity: severityTo } = getRiskStatusLabel(
    historyItem.to
  );
  const isStatusWithoutSeverityFrom = RiskStatusWithoutSeverity.includes(
    historyItem.from as RiskStatus
  );

  const isStatusChanged = statusFrom !== statusTo;
  const isSeverityChanged = severityFrom !== severityTo;
  const isBothChanged = isSeverityChanged && isStatusChanged;

  const by = getAccessEmail(accesss, historyItem?.by);

  const severity = (
    <>
      <p className="inline">
        {by ? (
          <span>
            {by} changed the <span className="font-semibold">Severity</span>{' '}
            {isStatusWithoutSeverityFrom ? 'to' : 'from'}
          </span>
        ) : (
          <span>
            <span className="font-semibold">Severity</span>
            {isStatusWithoutSeverityFrom ? ' set to' : ' changed from'}
          </span>
        )}
        {EmptySpace}
      </p>
      {!isStatusWithoutSeverityFrom && severityFrom && (
        <>
          <RiskTag severity={severityMap[severityFrom]} size="sm" />
          <p className="inline">
            {EmptySpace}to{EmptySpace}
          </p>
        </>
      )}
      {severityTo && <RiskTag severity={severityMap[severityTo]} size="sm" />}
    </>
  );

  const status = (
    <>
      <p className="inline">
        {isBothChanged ? (
          <>
            {EmptySpace}, and <span className="font-semibold">Status</span> from
            {EmptySpace}
          </>
        ) : (
          <>
            {by ? (
              <span>
                {by} changed the <span className="font-semibold">Status</span>{' '}
                from
              </span>
            ) : (
              <span>
                <span className="font-semibold">Status</span> changed from
              </span>
            )}
            {EmptySpace}
          </>
        )}
      </p>
      <BaseTag size="sm">
        {RiskStatusLabel[historyItem?.from?.[0] as RiskStatus] ?? 'Unknown'}
      </BaseTag>
      <p className="inline-block">
        {EmptySpace}to{EmptySpace}
      </p>
      <BaseTag size="sm">
        {RiskStatusLabel[historyItem?.to?.[0] as RiskStatus] ?? 'Unknown'}
      </BaseTag>
    </>
  );

  const machineCommentNode = getMachineRiskComment(historyItem)?.comment;
  let displayedCommentContent: ReactNode = null;

  if (machineCommentNode) {
    displayedCommentContent = machineCommentNode;
  } else if (historyItem.comment) {
    const commentString = String(historyItem.comment);
    if (isLikelyMarkdown(commentString)) {
      displayedCommentContent = (
        <div className="-mb-4">
          <MarkdownPreview source={commentString?.trim()} />
        </div>
      );
    } else {
      // Render as plain text, similar to original approach with quotes and word-break
      displayedCommentContent = (
        <i
          style={{ wordBreak: 'break-word' }}
        >{` "${commentString?.trim()}"`}</i>
      );
    }
  }

  const comment = displayedCommentContent ? (
    isStatusChanged || isSeverityChanged ? (
      <p className="inline">
        {` with `}
        <span className="font-semibold">Note</span>
        {EmptySpace}
        {displayedCommentContent}
      </p>
    ) : (
      <p className="inline">
        <span>
          {by} added the
          <span className="font-semibold">
            {'\xa0'}Note{'\xa0'}
          </span>
          {displayedCommentContent}
        </span>
      </p>
    )
  ) : null;

  const hasChange =
    isStatusChanged || isSeverityChanged || !!displayedCommentContent;

  return {
    title: hasChange ? (
      <div className="leading-7 whitespace-break-spaces">
        {(isSeverityChanged || isBothChanged) && severity}
        {(isStatusChanged || isBothChanged) && status}
        {comment}
      </div>
    ) : null,
    updated: formatDate(historyItem.updated),
  };
}
